/**
 * RFC 4180 serialization for the ASK AI CSV download feature.
 *
 * Pure functions over a `CellTextGrid`. Every CSV file the feature emits — the
 * header record and the data records, the synchronous path and the chunked
 * path, both chat surfaces — passes through `serializeCsv`, so there is no
 * route from a grid to a file that skips the formula neutralization below.
 *
 * Nothing here reads the DOM or touches React. `csvBlob` is the one function
 * that touches a browser API, and it only wraps an already-serialized string.
 *
 * Note that a `CellTextGrid` holds cell text as displayed and carries no
 * neutralization apostrophe. Adding the apostrophe here, rather than upstream
 * in `tableToCellTextGrid.ts`, is what lets the round-trip property (Req 3.12)
 * state an equality against the grid.
 */

import { CellTextGrid, CSV_BOM } from "./types";

// ── Serialization constants ────────────────────────────────────────────────

/** Separator between the fields of one CSV record. */
const FIELD_SEPARATOR = ",";

/**
 * Terminator written after every CSV record, the last one included (Req 3.10).
 */
const RECORD_TERMINATOR = "\r\n";

/** Character that makes a spreadsheet application read a cell as literal text. */
const NEUTRALIZATION_PREFIX = "'";

/** MIME type of the emitted Blob, matching the existing weblab tracker export. */
const CSV_MIME_TYPE = "text/csv;charset=utf-8;";

/**
 * First characters that make a spreadsheet application read a cell as a
 * formula (Req 3.13): equals sign (U+003D), commercial at (U+0040), character
 * tabulation (U+0009), and carriage return (U+000D).
 *
 * A leading tab or CR cannot in practice reach this module from a table-derived
 * grid, because Req 3.4 trims cell text. Those two entries are defense in depth
 * for any other producer of a `CellTextGrid`, including the deferred
 * server-side export tool.
 */
export const FORMULA_LEAD_CHARS = ["=", "@", "\t", "\r"] as const;

/**
 * Membership view of `FORMULA_LEAD_CHARS`, so the lookup accepts an arbitrary
 * string rather than only the four literal types of the exported tuple.
 */
const FORMULA_LEAD_CHAR_SET: ReadonlySet<string> = new Set(FORMULA_LEAD_CHARS);

/**
 * Characters whose presence in a field requires RFC 4180 enclosure (Req 3.6).
 *
 * The apostrophe is deliberately absent: neutralization therefore never changes
 * *whether* a field is enclosed, only what sits inside the enclosure.
 */
const ENCLOSURE_REQUIRED = /[",\r\n]/;

/**
 * The `Numeric_Remainder` grammar from Requirement 3, as one anchored regular
 * expression (Req 3.14, 3.15):
 *
 * ```
 * Numeric_Remainder := Space* CurrencySymbol? Space* Magnitude ( Space* Trailer )? Space*
 * Magnitude         := GroupedInteger ( "." Digit+ )?  |  "." Digit+
 * GroupedInteger    := Digit{1,3} ( "," Digit{3} )+  |  Digit+
 * Trailer           := "%" | CurrencySymbol
 * CurrencySymbol    := one character in the Unicode general category Sc
 * Space             := U+0020 or U+00A0
 * ```
 *
 * The `u` flag is required for `\p{Sc}`. `\d` is ASCII-only in JavaScript
 * regardless of the flag, which is exactly the grammar's `Digit` range
 * U+0030 through U+0039.
 *
 * The grouped alternative is written before the ungrouped one so `1,234.56`
 * matches as three-digit groups rather than failing after `\d+` consumes `1`.
 */
const NUMERIC_REMAINDER =
  /^[ \u00A0]*\p{Sc}?[ \u00A0]*(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?|\.\d+)(?:[ \u00A0]*(?:%|\p{Sc}))?[ \u00A0]*$/u;

// ── Formula neutralization ─────────────────────────────────────────────────

/**
 * Whether a character sequence is a `Numeric_Remainder` — the magnitude part
 * of a signed numeric value, as defined by the grammar under Requirement 3
 * "Definition of Numeric_Remainder".
 *
 * This is exported separately from `neutralizeField` on purpose: the numeric
 * exemption can then be reviewed and tested on its own, which matters because a
 * bug here mangles every negative number and every negative percentage the
 * product displays.
 *
 * The grammar is conservative by construction. It recognizes the comma as the
 * grouping separator and the period as the decimal separator, which are the
 * formats Req 3.5 emits; a sequence in another locale's convention fails to
 * match and its field is neutralized. That is the safe direction — the field
 * reaches the spreadsheet as text rather than as a formula.
 *
 * @param remainder The characters following a leading `+` or `-`.
 * @returns True when the sequence expresses a numeric magnitude. False for the
 *   empty sequence and for a lone `.`, so a field holding only `-` or `-.`
 *   is neutralized and reaches the spreadsheet as the characters it displays.
 */
export function isNumericRemainder(remainder: string): boolean {
  return NUMERIC_REMAINDER.test(remainder);
}

/**
 * Field text with one leading apostrophe where Req 3.13, 3.14, or 3.16 names
 * it, and unchanged otherwise (Req 3.17, 3.18).
 *
 * Three clauses add the apostrophe:
 *   - the first character is one of `FORMULA_LEAD_CHARS` (Req 3.13);
 *   - the first character is `+` or `-` and what follows is not a
 *     `Numeric_Remainder` (Req 3.14);
 *   - the first character is already an apostrophe (Req 3.16).
 *
 * The `+`/`-` clause is the load-bearing one. The textbook form of this
 * mitigation prefixes every field starting with `=`, `+`, `-`, or `@`, which in
 * a finance product would prefix every negative amount and every negative
 * percentage — a larger defect than the vulnerability it closes. So
 * `-1,234.56`, `-0.3%`, `+5`, and `-$1,234.56` pass through
 * character-for-character while `-2+3+cmd|' /C calc'!A0` does not. A
 * parenthesized negative such as `(1.2)%` starts with a left parenthesis, which
 * no clause names, so it is returned unchanged without the grammar being
 * consulted at all.
 *
 * The apostrophe clause carries no formula risk; it is what makes the
 * transformation exactly invertible. Taken with Req 3.17 it guarantees that a
 * field this function left alone never begins with an apostrophe, so "field
 * begins with an apostrophe" and "this function prefixed the field" are the
 * same condition and removing exactly one leading apostrophe recovers the
 * field's text in every case. That is the invariant Req 3.12 relies on.
 *
 * @param text The field's text, as displayed in the table.
 * @returns The text, prefixed with one apostrophe where a clause names it.
 *   The empty string has no first character and is returned unchanged.
 */
export function neutralizeField(text: string): string {
  const first = text.charAt(0);

  if (first === "") {
    return text;
  }

  if (FORMULA_LEAD_CHAR_SET.has(first) || first === NEUTRALIZATION_PREFIX) {
    return NEUTRALIZATION_PREFIX + text;
  }

  if (first === "+" || first === "-") {
    return isNumericRemainder(text.slice(1))
      ? text
      : NEUTRALIZATION_PREFIX + text;
  }

  return text;
}

// ── RFC 4180 enclosure ────────────────────────────────────────────────────

/**
 * One field enclosed in double quotes where RFC 4180 requires it (Req 3.6).
 *
 * Only ever called on the output of `neutralizeField`, never on raw cell text.
 * That ordering is Req 3.19 and it is not incidental — see `serializeCsv`.
 *
 * @param field An already-neutralized field.
 * @returns The field, enclosed and with embedded double quotes doubled when it
 *   holds a comma, a double quote, a carriage return, or a line feed.
 */
function quoteField(field: string): string {
  if (!ENCLOSURE_REQUIRED.test(field)) {
    return field;
  }
  return `"${field.replace(/"/g, '""')}"`;
}

// ── Grid to CSV text ──────────────────────────────────────────────────────

/**
 * RFC 4180 text for a grid, including the leading byte order mark.
 *
 * The field pipeline is `quoteField(neutralizeField(text))`, and the
 * composition order is load-bearing (Req 3.19). Prefixing an apostrophe never
 * changes *whether* enclosure is needed, because an apostrophe is not a comma,
 * a double quote, a CR, or an LF — so the enclosure decision is the same either
 * way. But the apostrophe has to end up *inside* the enclosure. Quoting first
 * would emit `'"=a,b"` for the cell text `=a,b`, which an RFC 4180 parser reads
 * as the two fields `'"=a` and `b"` instead of the one field it came from.
 *
 * The byte order mark is kept inside the returned string rather than added at
 * Blob construction time, which is what makes Req 3.11 and the round trip of
 * Req 3.12 testable on this one pure function.
 *
 * @param grid Rectangular grid of cell texts. `grid[0]` becomes the header
 *   record (Req 3.2); rows and fields keep their order (Req 3.1, 3.3).
 * @returns The CSV text: byte order mark, then one CRLF-terminated record per
 *   grid row, the last record included (Req 3.10, 3.11).
 */
export function serializeCsv(grid: CellTextGrid): string {
  const records = grid.map(
    (row) =>
      row
        .map((text) => quoteField(neutralizeField(text)))
        .join(FIELD_SEPARATOR) + RECORD_TERMINATOR
  );

  return CSV_BOM + records.join("");
}

/**
 * A `text/csv` Blob for already-serialized CSV text.
 *
 * The type string matches the existing weblab tracker export
 * (`WeblabTrackerContent.tsx`). The byte order mark is already part of
 * `csvText`, so nothing is prepended here.
 *
 * @param csvText Output of `serializeCsv`.
 * @returns A Blob the browser download handoff can hand to an anchor.
 */
export function csvBlob(csvText: string): Blob {
  return new Blob([csvText], { type: CSV_MIME_TYPE });
}
